/**
 * What the selected model states about the deal — the panel the Underwriting
 * tab was missing.
 *
 * A reviewer picked a model and the tab showed them cell counts. This shows
 * them the model: one row per deal field it states, the sheet and cell it
 * came from, what the deal currently holds, and the difference. A person can
 * adopt a figure onto the deal from here — deliberately, one field at a time,
 * with the cell recorded in the field's lineage — or leave it as the model's
 * claim and argue with it.
 */
import { useMemo, useState } from 'react'
import { ArrowDownToLine, CheckCircle2, Loader2, Sigma } from 'lucide-react'
import type { ModelDealView as ModelDealViewData } from '@/services/underwritingWorkbooks'
import { EditableValue } from './EditableValue'
import { formatFieldValue } from './format'
import { formatDelta } from './modelDelta'

const DASH = '—'

export function ModelDealView({
  view,
  loading,
  error,
  unlocked,
  adopting,
  adoptError,
  onAdopt,
  compact = false,
}: {
  view: ModelDealViewData | null | undefined
  loading: boolean
  error: string | null
  unlocked: boolean
  /** The field currently being adopted, if any. */
  adopting: string | null
  adoptError: string | null
  onAdopt: (field: string) => void
  /** Fewer rows, no unmatched list — for the dashboard card. */
  compact?: boolean
}) {
  const [showAll, setShowAll] = useState(false)
  const [differencesOnly, setDifferencesOnly] = useState(false)

  const rows = useMemo(() => {
    if (!view) return []
    const all = view.sections.flatMap((section) => section.rows.map((row) => ({ section: section.label, row })))
    return differencesOnly ? all.filter(({ row }) => row.delta === null || !row.delta.agrees) : all
  }, [view, differencesOnly])

  if (loading) {
    return (
      <p className="flex items-center gap-1.5 text-[9.5px] text-stone-500">
        <Loader2 size={10} className="animate-spin" /> Reading the model…
      </p>
    )
  }
  if (error) return <p className="text-[9.5px] text-red-300">{error}</p>
  if (!view) {
    return (
      <p className="text-[9.5px] leading-relaxed text-stone-600">
        Select a model above and this panel reads what it states about the deal.
      </p>
    )
  }
  if (view.coverage.cellsMapped === 0) {
    return (
      <p className="text-[9.5px] leading-relaxed text-stone-500">
        <span className="text-stone-300">{view.filename}</span> has no mapped cells yet, so nothing
        in it can be read against the deal. Sweep it for anchors from the model card.
      </p>
    )
  }
  if (rows.length === 0 && !differencesOnly) {
    return (
      <p className="text-[9.5px] leading-relaxed text-stone-500">
        {view.coverage.cellsMapped.toLocaleString()} cells are mapped in{' '}
        <span className="text-stone-300">{view.filename}</span>, but none names a numeric deal
        field. The metrics it states are listed under &ldquo;stated, no deal column&rdquo;.
      </p>
    )
  }

  const limit = compact && !showAll ? 8 : Infinity
  const shown = rows.slice(0, limit)
  const hidden = rows.length - shown.length

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2 text-[9px] text-stone-500">
        <span>
          <span className="font-mono text-stone-200">{view.coverage.fieldsStated}</span> of{' '}
          <span className="font-mono text-stone-300">{view.coverage.fieldsApplicable}</span> deal fields stated
          <span className="text-stone-700"> · </span>
          {view.coverage.cellsMapped.toLocaleString()} of {view.coverage.cellsImported.toLocaleString()} cells mapped
          <span className="text-stone-700"> · </span>
          <span className="text-stone-400">v{view.versionNumber} {view.filename}</span>
        </span>
        <label className="inline-flex cursor-pointer items-center gap-1">
          <input
            type="checkbox"
            checked={differencesOnly}
            onChange={(event) => setDifferencesOnly(event.target.checked)}
            className="h-3 w-3 accent-amber-400"
          />
          differences only
        </label>
      </div>

      <table className="w-full border-collapse text-[9.5px]" aria-label="What this model states">
        <thead>
          <tr className="text-left text-[8px] uppercase tracking-wide text-stone-600">
            <th className="pb-1 font-medium">Field</th>
            <th className="pb-1 text-right font-medium">Model</th>
            <th className="pb-1 text-right font-medium">Deal</th>
            <th className="pb-1 text-right font-medium">Difference</th>
            <th className="pb-1 font-medium">Cell</th>
            <th className="pb-1" />
          </tr>
        </thead>
        <tbody>
          {shown.map(({ section, row }) => (
            <tr key={row.field} className="border-t border-stone-900 align-top" data-field={row.field}>
              <td className="py-1 pr-2">
                <div className="text-stone-200">{row.label}</div>
                <div className="text-[8px] text-stone-600">{section}</div>
              </td>
              <td className="py-1 pr-2 text-right font-mono text-stone-100" title={row.provenance.computed ? 'A formula cell — the model’s own conclusion' : 'A typed input'}>
                {formatFieldValue(row.modelValue, row.kind)}
                {row.provenance.computed ? <Sigma size={8} className="ml-1 inline text-stone-600" aria-label="computed" /> : null}
              </td>
              <td className="py-1 pr-2 text-right font-mono text-stone-400">
                {/* The deal's own value, edited here once unlocked like every other
                    value in the dossier; a derived one opens its inputs. */}
                <EditableValue
                  name={row.field}
                  label={row.label}
                  display={row.dealValue === null || row.dealValue === undefined ? DASH : formatFieldValue(row.dealValue, row.kind)}
                />
              </td>
              <td
                className={`py-1 pr-2 text-right font-mono ${
                  row.dealFromThisModel
                    ? 'text-emerald-300'
                    : row.delta === null
                      ? 'text-stone-600'
                      : row.delta.agrees
                        ? 'text-stone-500'
                        : 'text-amber-300'
                }`}
              >
                {row.dealFromThisModel ? 'on the deal' : row.delta === null ? 'not on deal' : formatDelta(row)}
              </td>
              <td className="py-1 pr-2 font-mono text-[8.5px] text-stone-500" title={row.provenance.label ?? undefined}>
                {row.provenance.sheet}!{row.provenance.cell}
                {row.alternatives.length ? (
                  <span className="ml-1 text-amber-400/80" title={row.alternatives.map((alt) => `${alt.sheet}!${alt.cell} = ${alt.value}`).join(', ')}>
                    +{row.alternatives.length}
                  </span>
                ) : null}
              </td>
              <td className="py-1 text-right">
                {/* The server says which figures can be adopted (`adoptable`): a figure
                    the deal already holds from this model shows as adopted (UI-09). */}
                {row.dealFromThisModel || !row.adoptable ? (
                  <CheckCircle2 size={11} className="inline text-emerald-400" aria-label="adopted" />
                ) : (
                  <button
                    type="button"
                    disabled={!unlocked || adopting !== null}
                    onClick={() => onAdopt(row.field)}
                    title={
                      unlocked
                        ? `Adopt ${row.label} from ${row.provenance.sheet}!${row.provenance.cell} onto the deal`
                        : 'Unlock the dossier to adopt a figure'
                    }
                    className="inline-flex items-center gap-1 rounded border border-amber-400/25 px-1.5 py-0.5 text-[8.5px] text-amber-200 transition-colors hover:bg-amber-400/[0.09] disabled:cursor-not-allowed disabled:border-stone-800 disabled:text-stone-600"
                  >
                    {adopting === row.field ? <Loader2 size={9} className="animate-spin" /> : <ArrowDownToLine size={9} />}
                    Adopt
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {hidden > 0 ? (
        <button type="button" onClick={() => setShowAll(true)} className="text-[9px] text-amber-300 hover:underline">
          Show {hidden} more
        </button>
      ) : null}
      {adoptError ? <p className="text-[9.5px] text-red-300">{adoptError}</p> : null}

      {!compact && view.unmatched.length ? (
        <details className="pt-1">
          <summary className="cursor-pointer text-[9px] text-stone-500">
            {(() => {
              // The full count, with the listed slice named when the server cut it (UI-12).
              const total = view.unmatchedTotal ?? view.unmatched.length
              const head = `${total} metric${total === 1 ? '' : 's'} stated, no deal column`
              return total > view.unmatched.length ? `${head} · showing ${view.unmatched.length}` : head
            })()}
          </summary>
          <ul className="mt-1 grid grid-cols-1 gap-x-4 gap-y-0.5 text-[9px] text-stone-500 sm:grid-cols-2">
            {view.unmatched.map((item) => (
              <li key={`${item.sheet}-${item.cell}-${item.semanticKey}`} className="flex justify-between gap-2">
                <span className="truncate" title={item.label ?? item.semanticKey}>{item.label ?? item.semanticKey}</span>
                <span className="shrink-0 font-mono text-stone-400">
                  {formatFieldValue(item.value, 'number')} <span className="text-stone-700">{item.sheet}!{item.cell}</span>
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  )
}
